import { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle, Mail, Building2, TrendingUp, User } from "lucide-react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { API_BASE_URL, BACKEND_URL } from "../lib/site";

const API = API_BASE_URL;

const STAGES = ["0 → 1 (Pre-PMF)", "1 → 10 (Scaling)", "Series A+", "Other"];

const TOPICS = [
  "Brand strategy & positioning",
  "Performance marketing (Meta/Google)",
  "CRM & customer lifecycle",
  "D2C / marketplace growth",
  "AI in marketing",
  "Full-stack growth system",
  "Other",
];

export default function BookCallPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    stage: "",
    topic: "",
    challenge: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="max-w-lg mx-auto px-6 text-center py-16 sm:py-20" data-testid="lead-success">
          <div className="w-16 h-16 bg-[#002FA7] flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={32} className="text-white" />
          </div>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-3">
            Got it — I'll be in touch.
          </h2>
          <p className="text-[#4B5563] leading-relaxed mb-8 max-w-sm mx-auto">
            Your message is with me. I personally review every request and respond within 24–48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/writing" className="border border-[#0A0A0A] text-[#0A0A0A] px-6 py-3 text-sm font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors">
              Read my writing
            </Link>
            <Link to="/case-studies" className="bg-[#002FA7] text-white px-6 py-3 text-sm font-semibold hover:bg-[#001D66] transition-colors">
              View case studies
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 page-enter">
      <SEOHead
        title="Work With Me — B2C Growth Consulting"
        description="Engage Niharika Patel for growth & marketing strategy. Working with B2C startups at 0→1 and 1→10 stages. Brand, performance marketing, CRM. Tell me about your challenge."
        url={`${BACKEND_URL}/book`}
        schema={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Niharika Patel — Growth & Marketing Consulting",
          "description": "B2C startup marketing and growth consulting. Integrated brand, performance, and CRM strategy.",
          "url": BACKEND_URL
        }}
      />
      <div className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 sm:py-16 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">Work With Me</p>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#0A0A0A] mb-5">
              Let's start<br /><span className="font-semibold">a conversation.</span>
            </h1>
            <p className="text-[#4B5563] text-lg leading-relaxed max-w-md">
              Tell me about your startup and what you're trying to solve. I review every submission personally and respond within 48 hours.
            </p>
          </div>

          {/* What to expect */}
          <div className="space-y-4">
            {[
              { icon: Mail, title: "Personal response", desc: "I read every submission and respond directly — no assistants, no templates." },
              { icon: TrendingUp, title: "Tailored thinking", desc: "I'll share an initial perspective on your challenge before we even speak." },
              { icon: Building2, title: "Right-fit only", desc: "I work with a limited number of startups at a time. Quality over volume." },
            ].map((item) => (
              <div key={item.title} className="flex gap-4 items-start">
                <div className="w-9 h-9 bg-[#002FA7]/8 border border-[#002FA7]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <item.icon size={15} className="text-[#002FA7]" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#0A0A0A]">{item.title}</p>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-2">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full border border-[#E5E7EB] pl-9 pr-3 py-3 text-sm focus:outline-none focus:border-[#002FA7] transition-colors"
                      placeholder="Your name"
                      data-testid="lead-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full border border-[#E5E7EB] pl-9 pr-3 py-3 text-sm focus:outline-none focus:border-[#002FA7] transition-colors"
                      placeholder="your@startup.com"
                      data-testid="lead-email"
                    />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-2">
                    Company / Startup
                  </label>
                  <div className="relative">
                    <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input
                      type="text"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="w-full border border-[#E5E7EB] pl-9 pr-3 py-3 text-sm focus:outline-none focus:border-[#002FA7] transition-colors"
                      placeholder="Startup name"
                      data-testid="lead-company"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-2">
                    Stage
                  </label>
                  <select
                    value={form.stage}
                    onChange={(e) => setForm({ ...form, stage: e.target.value })}
                    className="w-full border border-[#E5E7EB] px-3 py-3 text-sm focus:outline-none focus:border-[#002FA7] transition-colors bg-white"
                    data-testid="lead-stage"
                  >
                    <option value="">Select stage</option>
                    {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-2">
                  Primary area of focus
                </label>
                <div className="flex flex-wrap gap-2">
                  {TOPICS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm({ ...form, topic: form.topic === t ? "" : t })}
                      className={`text-xs px-3 py-2 border transition-colors ${
                        form.topic === t
                          ? "bg-[#002FA7] text-white border-[#002FA7]"
                          : "border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7] hover:text-[#002FA7]"
                      }`}
                      data-testid={`topic-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-2">
                  What's the one challenge you'd like solved? *
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.challenge}
                  onChange={(e) => setForm({ ...form, challenge: e.target.value })}
                  className="w-full border border-[#E5E7EB] px-4 py-3 text-sm focus:outline-none focus:border-[#002FA7] transition-colors resize-none"
                  placeholder="Be specific. The more context you give, the more useful my response will be. E.g. 'We have decent CAC on Meta but our 30-day retention is 18%. We've tried email re-engagement but it's not working...'"
                  data-testid="lead-challenge"
                />
                <p className="text-xs text-[#9CA3AF] mt-1">{form.challenge.length}/500</p>
              </div>

              {error && (
                <div className="border border-red-200 bg-red-50 text-red-600 text-sm px-4 py-3">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#002FA7] text-white py-4 font-semibold hover:bg-[#001D66] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                data-testid="lead-submit"
              >
                {loading ? (
                  <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Sending...</>
                ) : (
                  <>Send Message <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="border border-[#E5E7EB] p-5">
              <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-[#002FA7] mb-3">Who I Work With</p>
              <div className="space-y-3">
                {[
                  "B2C startups in 0→1 or 1→10 phase",
                  "Founders who want brand + performance + CRM as one system",
                  "Teams willing to move fast and test bold ideas",
                  "D2C, marketplace, quick commerce, consumer tech",
                ].map((item, i) => (
                  <div key={i} className="flex gap-2 text-sm text-[#4B5563]">
                    <span className="text-[#002FA7] font-bold flex-shrink-0">—</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-[#E5E7EB] p-5">
              <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-[#002FA7] mb-3">What I Bring</p>
              <div className="space-y-2">
                {[
                  ["~10 years", "across Amazon, Nestle, Zepto, Dabur and Emami"],
                  ["₹40Cr+", "monthly budgets managed"],
                  ["2x", "category growth delivered"],
                  ["43%", "avg. ROI improvement"],
                ].map(([v, l]) => (
                  <div key={v} className="flex items-baseline gap-2">
                    <span className="font-['Outfit'] font-bold text-lg text-[#002FA7]">{v}</span>
                    <span className="text-xs text-[#6B7280]">{l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
